import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Phone } from "lucide-react";
import { PHONE_LINK } from "@/components/Ui";

const HERO_IMG = "/realisations/gros-oeuvre-batiment.webp";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="accueil" ref={ref} className="relative min-h-screen overflow-hidden flex items-center" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
        <img src={HERO_IMG} alt="Travaux de maçonnerie à Bernay : gros œuvre en parpaings réalisé par DMZ Normandie" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-[#0A0A0A]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-xs md:text-sm font-semibold tracking-[0.5em] uppercase text-[#C4A484] mb-8"
          data-testid="hero-overline"
        >
          DMZ Normandie
        </motion.p>

        <div className="font-serif-display font-light leading-[1.02] text-[11vw] md:text-[7.5vw] lg:text-[5.8vw] tracking-tight" aria-hidden="true">
          <span className="hero-line-mask block">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}>
              Façonner la matière
            </motion.span>
          </span>
          <span className="hero-line-mask block">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.68, ease: [0.16, 1, 0.3, 1] }}>
              Bâtir <em className="text-accent-sand not-italic">l'éternité</em>
            </motion.span>
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 text-base md:text-xl font-medium tracking-wide text-[#F5F5F0]"
          data-testid="hero-headline"
        >
          Entreprise de maçonnerie à Bernay, dans l'Eure
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-4 max-w-2xl text-sm md:text-base font-light text-zinc-400 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Maçonnerie générale, rénovation, gros œuvre, extensions, fondations, murs et ouvrages
          en béton à Bernay, Évreux et dans toute la Normandie.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link to="/contact" className="btn-sable" data-testid="hero-devis-button">
            Demander un devis gratuit <ArrowRight size={16} />
          </Link>
          <a href={PHONE_LINK} className="btn-outline-light" data-testid="hero-call-button">
            <Phone size={16} /> Appeler DMZ Normandie
          </a>
        </motion.div>

        <motion.a
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          href="#realisations"
          data-testid="hero-scroll-cta"
          className="group mt-14 inline-flex items-center gap-4 text-xs tracking-[0.3em] uppercase text-zinc-500 hover:text-[#F5F5F0] transition-colors duration-300"
        >
          Découvrir nos réalisations
          <span className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center group-hover:border-[#C4A484] transition-colors duration-300">
            <ArrowDown size={14} className="animate-bounce" />
          </span>
        </motion.a>
      </motion.div>
    </section>
  );
}
